import { doc, onSnapshot } from "firebase/firestore";
import {
  CreateParams,
  CreateResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
} from "react-admin";
import type { z } from "zod";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { firestore } from "../firebase";
import { ThreadSchema, type Post, type Thread } from "../model/Thread.js";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { gcs } from "../utils/storage";
import { threadsProvider } from "./threadsProvider";

type CreatePostDto = z.infer<typeof schemas.CreatePostDto>;
type UpdatePostDto = z.infer<typeof schemas.UpdatePostDto>;

class PostsProvider extends AbstractDataProvider<Post> {
  constructor() {
    super();
  }

  async update(_: string, { id, data: { body } }: UpdateParams<Post>): Promise<UpdateResult<Post>> {
    const [threadId, postId] = id.split(":");
    if (!body || !threadId) {
      throw new Error("Properties body and threadId must be provided.");
    }
    const postUpdated = new Promise<void>((resolve, reject) => {
      const unsubscribe = this.onUpdate(threadId, async (thread) => {
        try {
          if (thread.posts[postId].body === body) {
            unsubscribe();
            resolve();
          }
        } catch (error) {
          unsubscribe();
          reject(error);
        }
      });
    });
    await this.updatePost(postId, { body });
    await postUpdated;
    const updatedPost = await this.fetch(id);
    return { data: updatedPost };
  }

  async create(
    _: string,
    {
      data: { body, files, threadId },
    }: CreateParams<{
      threadId: string;
      body: string;
      files?: Array<File>;
    }>,
  ): Promise<CreateResult<Post>> {
    if (!body || !threadId) {
      throw new Error("Properties body and threadId must be provided.");
    }
    const attachments = files && files.length > 0 ? await gcs.uploadFiles(files) : [];
    let post: Post | undefined;
    const postCreated = new Promise<void>((resolve, reject) => {
      const unsubscribe = this.onUpdate(threadId, async (thread) => {
        try {
          post = Object.values(thread.posts).find(
            (it) => it.body === body && !it.deleted && it.attachments?.length === attachments.length,
          );
          if (post) {
            unsubscribe();
            resolve();
          }
        } catch (error) {
          unsubscribe();
          reject(error);
        }
      });
    });
    await this.createPost({ threadId, body, attachments });
    await postCreated;
    const createdPost = await this.fetch(`${threadId}:${post!.id}`);
    return { data: createdPost };
  }

  async delete(_: string, { id }: DeleteParams<{ id: string }>): Promise<DeleteResult> {
    const [threadId, postId] = id.split(":");
    const postDeleted = new Promise<void>((resolve, reject) => {
      const unsubscribe = this.onUpdate(threadId, async (thread) => {
        try {
          const posts = thread.posts;
          if (!posts[postId] || posts[postId].deleted) {
            unsubscribe();
            resolve();
          }
        } catch (error) {
          unsubscribe();
          reject(error);
        }
      });
    });
    await this.deletePost(postId);
    await postDeleted;
    return { data: {} };
  }

  private async updatePost(id: string, data: UpdatePostDto) {
    const client = await getAuthenticatedServerClient();
    return await client.updatePost(data, { params: { id } });
  }

  private async createPost(data: CreatePostDto) {
    const client = await getAuthenticatedServerClient();
    return await client.createPost(data);
  }

  private async deletePost(id: string) {
    const client = await getAuthenticatedServerClient();
    await client.deletePost(undefined, { params: { id } });
  }

  async getOne(_: string, { id }: GetOneParams<Post>): Promise<GetOneResult<Post>> {
    return { data: await this.fetch(id) };
  }

  async fetch(id: Identifier): Promise<Post> {
    const [threadId, postId] = id.toString().split(":");
    const thread = await threadsProvider.getOne("", { id: threadId });
    return thread.data.posts[postId];
  }

  onUpdate(threadId: string, listener: (thread: Thread) => void): () => void {
    const unsubscribe = onSnapshot(doc(firestore, `threads/${threadId}`), (snapshot) => {
      const data = snapshot.data();
      listener(ThreadSchema.parse(data));
    });
    return unsubscribe;
  }
}

export const postsProvider = new PostsProvider();
